







































































import { Component, Inject, Watch, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import HasValidation from "@/mixins/HasValidation";
import PaymentProvider from "@/components/app/global/payments/selectPaymentMethod/provider";

// Components
import CheckoutControl from "@/components/app/global/baskets/basket/checkout/checkoutControl.vue";
import BillingDetails from "@/components/app/global/baskets/basket/checkout/billingDetails.vue";
import GuestEmail from "@/components/app/global/baskets/basket/checkout/guestEmail.vue";
import SelectPaymentMethod from "@/components/app/global/payments/selectPaymentMethod/index.vue";
import ExternalStoreMixin from "@/components/app/admin/oauth/externalStoreMixin";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { BrandConfigKeys, QUERY_PARAMS } from "@/data/constants";
import { Methods } from "@/models/methods";
import { GatewayStoreType } from "@/data/enums/gateway";

// Types
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import { IInvoice } from "@/models/invoices";
import { IState } from "@/store";
import { IClient } from "@/models/clients";

@Component({
  components: {
    CheckoutControl,
    BillingDetails,
    GuestEmail,
    SelectPaymentMethod
  },
  computed: {
    ...mapState({
      user: (state: IState) => state.user
    })
  }
})
export default class BasketCheckout extends Mixins(
  PaymentProvider,
  ExternalStoreMixin,
  HasValidation
) {
  public user!: IClient;

  // Inject: basket data
  @Inject({ from: "providerData" })
  public readonly $providerData!: () => IBasketProviderData;
  @Inject({ from: "providerMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  public get basketData() {
    return this.$providerData();
  }

  // Data
  public basketsDataModule: DataModules = DataModules.BASKETS;
  public isProcessing = false;
  public invoice = {} as IInvoice;
  public guestEmail = "";
  public failSilentIfObserverNotFound = true; // Remove observer now forund when redirecting

  public created() {
    if (!this.basket) {
      if (this.basketData.basketId !== "new") {
        this.$store.dispatch("toast/error", {
          message: this.$t("_sentence.basket_no_longer_exists")
        });
      }

      this.$basketMethods.goToShop();
    }

    this.checkExternalStoreQueryParams();
  }

  public get basket() {
    return this.basketData.basket;
  }

  public get attemptKey() {
    return btoa(
      JSON.stringify({
        invoiceId: this.basketData.basketId,
        timestamp: this.$moment()
          .toDate()
          .getTime()
      })
    );
  }

  public async validateBeforeSubmit() {
    this.isProcessing = true;
    this.$basketMethods.setIsProcessing(true);

    try {
      if (!(await this.validate())) throw this.$t("_.form_invalid");

      if (this.user.is_guest && this.guestEmail.length) {
        await this.updateGuestEmail();
      }

      await this.preparePaymentData(this.basketData.clientId);

      if (this.isExternalStore) return;

      // Set key BEFORE converting invoice (incase 0.00 total === paid)
      const attemptKey = this.attemptKey;

      this.invoice = (await this.$store.dispatch(
        `data/${this.basketsDataModule}/convertToInvoice`,
        {
          id: this.basket.id,
          isClient: !this.isAdmin,
          data: this.paymentData
        }
      )) as IInvoice;

      try {
        await this.executePayment(attemptKey);
        if (!this.$_.get(this.paymentMethod, "data.external", false)) {
          this.$basketMethods.goToComplete({
            ...(this.isAdmin
              ? {}
              : { [QUERY_PARAMS.ORDER_ID]: this.invoice.id }),
            [QUERY_PARAMS.ATTEMPT]: attemptKey
          });
        }
      } catch (error) {
        this.$basketMethods.goToComplete({
          ...(this.isAdmin ? {} : { [QUERY_PARAMS.ORDER_ID]: this.invoice.id }),
          [QUERY_PARAMS.PAYMENT_SUCCESS]: false
        });
      }
    } catch (error) {
      this.isProcessing = false;
      this.$basketMethods.setIsProcessing(false);
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }
  }

  public get totalAmountToPay() {
    return this.basket?.unpaid_amount_converted;
  }

  public generateReturnUrl() {
    const location = this.$router.resolve({
      name: this.isAdmin ? "adminOrderComplete" : "orderCompleteStep"
    });
    return `${window.location.origin}${location.href}?${QUERY_PARAMS.ORDER_ID}=${this.invoice.id}&${QUERY_PARAMS.ATTEMPT}=${this.attemptKey}`;
  }

  public generateReturnUrlExternalStore() {
    const vueLocation = this.$router.resolve({
      name: this.isAdmin ? "adminOrderCheckout" : "orderCheckoutStep",
      params: {
        basketId: this.basket.id
      }
    });
    return `${window.location.origin}${vueLocation.href}`;
  }

  public updateGuestEmail() {
    return this.$store.dispatch("api/call", {
      method: Methods.PUT,
      path: `api/clients/${this.user.id}`,
      requestConfig: { data: { email: this.guestEmail } }
    });
  }

  public get oauthReturnUrl() {
    return this.generateReturnUrlExternalStore();
  }

  public get additionalPaymentData() {
    const returnUrl = this.generateReturnUrl();
    const returnUrlEncodedSuccess = encodeURIComponent(
      `${returnUrl}&${QUERY_PARAMS.PAYMENT_SUCCESS}=true`
    );
    const returnUrlEncodedFail = encodeURIComponent(
      `${returnUrl}&${QUERY_PARAMS.PAYMENT_SUCCESS}=false`
    );

    return {
      client_id: this.basketData.clientId,
      account_id: this.basketData.accountId,
      invoice_id: this.$_.get(this.invoice, "id", "").toString(),
      return_url: `?${QUERY_PARAMS.SUCCESS}=${returnUrlEncodedSuccess}&${QUERY_PARAMS.FAILED}=${returnUrlEncodedFail}`,
      cancel_url: returnUrl
    };
  }

  public get excludedGatewayStoreTypes() {
    return this.user.is_guest ? [GatewayStoreType.ALWAYS] : [];
  }

  public get additionalManualPaymentData() {
    return {
      invoice_id: this.invoice.id
    };
  }

  public get hasPayLater() {
    return (
      this.isAdmin ||
      this.basketData.brandData.config[BrandConfigKeys.PAY_LATER_ENABLED]
    );
  }

  public get isDisabled() {
    if (!this.totalAmountToPay) return false;
    if (!this.paymentMethod) return true;
    return (
      !this.$_.has(this.paymentMethod, "type") &&
      this.$_.get(this.paymentMethod, "amount") !==
        this.$_.get(this.paymentMethod, "walletAmount")
    );
  }

  public get hasPartialPayment() {
    return (
      this.isAdmin ||
      this.basketData.brandData.config[BrandConfigKeys.PARTIAL_PAYMENTS_ENABLED]
    );
  }

  @Watch("basketData.isValidCheckoutPage", { immediate: true })
  public onValidCheckoutPageChange(value) {
    if (!value && !this.basketData.basketIsComplete) {
      return this.$basketMethods.goToBasketView();
    }
  }
}
